/**
 * User Synchronization with Supabase Auth
 *
 * This module provides the main API for all Supabase user operations.
 * Functions are organized into service modules for maintainability.
 *
 * Service Modules:
 * - xpService: XP and pomodoro stats operations
 * - pomodoroService: Pomodoro/break session operations
 * - rewardService: Milestone rewards and unlocks
 * - dailyGiftService: Daily gift claiming
 */

import { supabase } from './supabase';
import type { AppUser } from './supabaseAuth';

// ============================================
// RE-EXPORTS FROM SERVICE MODULES
// ============================================

// XP operations
export { incrementUserXP, incrementPomodoroTotals } from './services/xpService';

// Pomodoro/break operations
export {
  saveCompletedPomodoro,
  getRecentPomodoros,
  saveCompletedBreak,
  getUserStats,
  type CompletedPomodoro,
} from './services/pomodoroService';

// Reward operations
export {
  unlockMilestoneReward,
  getUserUnlockedRewards,
  isRewardUnlocked,
  type RewardType,
  type UnlockedReward,
} from './services/rewardService';

// Daily gift operations
export {
  claimDailyGift,
  canClaimDailyGift,
  type DailyGiftResult,
} from './services/dailyGiftService';

// ============================================
// USER SETTINGS & PREFERENCES
// ============================================

/**
 * Update user settings (legacy - use updateUserPreferences for full control)
 */
export async function updateUserSettings(
  userId: string,
  settings: {
    sound_enabled?: boolean;
    volume?: number;
    music_volume?: number;
    level_system_enabled?: boolean;
    level_path?: 'elf' | 'human';
  }
): Promise<void> {
  if (import.meta.env.DEV)
    console.log(`[User Sync] Updating settings for user ${userId}`, settings);

  const { error } = await supabase
    .from('users')
    .update({
      ...settings,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);

  if (error) {
    console.error('[User Sync] Error updating settings:', error);
    throw new Error('Failed to update settings');
  }

  if (import.meta.env.DEV) console.log('[User Sync] Settings updated successfully');
}

/**
 * Update user settings (CLIENT-CONTROLLED)
 *
 * SECURITY: Only syncs settings that users can safely control.
 * Does NOT sync XP, levels, or stats (those are server-controlled).
 */
export async function updateUserPreferences(
  userId: string,
  preferences: {
    // Timer preferences
    timer_pomodoro_minutes?: number;
    timer_short_break_minutes?: number;
    timer_long_break_minutes?: number;
    pomodoros_before_long_break?: number;
    auto_start_breaks?: boolean;
    auto_start_pomodoros?: boolean;

    // Visual preferences
    background_id?: string;
    background_mobile?: string;
    background_desktop?: string;
    playlist?: 'lofi' | 'synthwave';
    ambient_volumes?: Record<string, number>;
    auto_hide_ui?: boolean;

    // Audio preferences
    sound_enabled?: boolean;
    volume?: number;
    music_volume?: number;

    // System preferences
    level_system_enabled?: boolean;
    level_path?: 'elf' | 'human';
  }
): Promise<AppUser> {
  if (import.meta.env.DEV) console.log(`[User Sync] Updating user settings for user ${userId}`);

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Authentication required to update settings');
  }

  if (import.meta.env.DEV) console.log('[User Sync] Using authenticated session');

  const { data, error } = await supabase.rpc('update_user_settings', {
    p_user_id: userId,
    p_timer_pomodoro_minutes: preferences.timer_pomodoro_minutes ?? null,
    p_timer_short_break_minutes: preferences.timer_short_break_minutes ?? null,
    p_timer_long_break_minutes: preferences.timer_long_break_minutes ?? null,
    p_pomodoros_before_long_break: preferences.pomodoros_before_long_break ?? null,
    p_auto_start_breaks: preferences.auto_start_breaks ?? null,
    p_auto_start_pomodoros: preferences.auto_start_pomodoros ?? null,
    p_background_id: preferences.background_id ?? null,
    p_background_mobile: preferences.background_mobile ?? null,
    p_background_desktop: preferences.background_desktop ?? null,
    p_playlist: preferences.playlist ?? null,
    p_ambient_volumes: preferences.ambient_volumes ?? null,
    p_auto_hide_ui: preferences.auto_hide_ui ?? null,
    p_sound_enabled: preferences.sound_enabled ?? null,
    p_volume: preferences.volume ?? null,
    p_music_volume: preferences.music_volume ?? null,
    p_level_system_enabled: preferences.level_system_enabled ?? null,
    p_level_path: preferences.level_path ?? null,
  });

  if (error) {
    console.error('[User Sync] Error updating settings:', error);
    throw new Error(`Failed to update settings: ${error.message}`);
  }

  if (import.meta.env.DEV) console.log('[User Sync] Settings updated successfully');
  return data as AppUser;
}

// ============================================
// USER LOOKUP & VALIDATION
// ============================================

/**
 * Get user by auth user ID
 */
export async function getUserByAuthId(authUserId: string): Promise<AppUser | null> {
  if (import.meta.env.DEV) console.log(`[User Sync] Fetching user by auth ID: ${authUserId}`);

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (error) {
    console.error('[User Sync] Error fetching user:', error);
    throw new Error('Failed to fetch user');
  }

  return data as AppUser | null;
}

/**
 * Check username availability
 */
export async function checkUsernameAvailability(username: string): Promise<boolean> {
  if (!username || username.trim().length === 0) return false;

  const { data, error } = await supabase.rpc('check_username_availability', {
    p_username: username,
  });

  if (error) {
    console.error('[User Sync] Error checking username availability:', error);
    return true; // Fail open (allow try) but log error
  }

  return data as boolean;
}

// ============================================
// LOGIN STREAK TRACKING
// ============================================

/**
 * Update user login streak
 */
export async function updateLoginStreak(userId: string): Promise<void> {
  if (import.meta.env.DEV) console.log(`[User Sync] Updating login streak for user ${userId}`);

  const today = new Date().toISOString().split('T')[0];

  // Get current streak data
  const { data: user, error: fetchError } = await supabase
    .from('users')
    .select('last_login_date, consecutive_login_days, total_unique_days')
    .eq('id', userId)
    .single();

  if (fetchError) {
    console.error('[User Sync] Error fetching streak data:', fetchError);
    return; // Non-fatal
  }

  const lastLoginDate = user.last_login_date;
  const updates: Record<string, unknown> = {
    last_login_date: today,
    last_login: new Date().toISOString(),
  };

  if (!lastLoginDate) {
    // First login ever
    updates.consecutive_login_days = 1;
    updates.total_unique_days = 1;
  } else if (lastLoginDate === today) {
    // Already logged in today - no updates needed
    return;
  } else {
    // Check if yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (lastLoginDate === yesterdayStr) {
      // Consecutive day
      updates.consecutive_login_days = user.consecutive_login_days + 1;
    } else {
      // Streak broken
      updates.consecutive_login_days = 1;
    }
    updates.total_unique_days = user.total_unique_days + 1;
  }

  const { error: updateError } = await supabase.from('users').update(updates).eq('id', userId);

  if (updateError) {
    console.error('[User Sync] Error updating streak:', updateError);
  } else {
    if (import.meta.env.DEV) console.log('[User Sync] Login streak updated:', updates);
  }
}

// ============================================
// USERNAME MANAGEMENT
// ============================================

/**
 * Update username with cooldown enforcement and optional XP cost
 */
export async function updateUsernameSecure(
  userId: string,
  newUsername: string,
  forceWithXP: boolean = false
): Promise<AppUser> {
  if (import.meta.env.DEV) {
    console.log(
      `[User Sync] Updating username for user ${userId} to: ${newUsername} (forceWithXP: ${forceWithXP})`
    );
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Authentication required to update username');
  }

  if (import.meta.env.DEV) console.log('[User Sync] Using authenticated session');

  const { data, error } = await supabase.rpc('update_username', {
    p_user_id: userId,
    p_new_username: newUsername,
    p_force_with_xp: forceWithXP,
  });

  if (error) {
    console.error('[User Sync] Error updating username:', error);
    throw new Error(`Failed to update username: ${error.message}`);
  }

  if (import.meta.env.DEV) console.log('[User Sync] Username updated successfully');
  return data as AppUser;
}

/**
 * Update username with cooldown enforcement (LEGACY)
 * @deprecated Use updateUsernameSecure instead
 */
export async function updateUsername(
  userId: string,
  newUsername: string,
  cooldownHours: number = 720
): Promise<{
  success: boolean;
  message: string;
  user?: AppUser;
}> {
  if (import.meta.env.DEV)
    console.log(`[User Sync] Updating username for user ${userId} to: ${newUsername}`);

  const { data, error } = await supabase.rpc('update_username_with_cooldown', {
    p_user_id: userId,
    p_new_username: newUsername,
    p_cooldown_hours: cooldownHours,
  });

  if (error) {
    console.error('[User Sync] Error updating username:', error);
    throw new Error(`Failed to update username: ${error.message}`);
  }

  const result = Array.isArray(data) ? data[0] : data;

  return {
    success: result.success,
    message: result.message,
    user: result.user_data as AppUser,
  };
}

// ============================================
// PROGRESS RESET
// ============================================

/**
 * Reset user progress (XP, level, prestige, stats)
 */
export async function resetUserProgress(userId: string): Promise<AppUser> {
  if (import.meta.env.DEV) console.log(`[User Sync] Resetting progress for user ${userId}`);

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Authentication required to reset progress');
  }

  if (import.meta.env.DEV) console.log('[User Sync] Using authenticated session');

  const { data, error } = await supabase.rpc('reset_user_progress', {
    p_user_id: userId,
  });

  if (error) {
    console.error('[User Sync] Error resetting progress:', error);
    throw new Error(`Failed to reset progress: ${error.message}`);
  }

  if (!data) {
    console.error('[User Sync] No data returned from reset_user_progress');
    throw new Error('Failed to reset progress: No data returned');
  }

  if (import.meta.env.DEV) console.log('[User Sync] Progress reset successfully');
  return data as AppUser;
}

// ============================================
// TIMEZONE MANAGEMENT
// ============================================

interface TimezoneChangeResponse {
  status: 'pending' | 'rejected';
  message: string;
  newTimezone?: string;
  newWeekendDays?: number[];
  appliesAt?: string;
  hoursUntilApplied?: number;
  changesRemainingThisMonth?: number;
  cooldownUntil?: string;
  hoursRemaining?: number;
  resetDate?: string;
}

/**
 * Request timezone change with server-side validation
 */
export async function requestTimezoneChange(
  userId: string,
  newTimezone: string,
  newWeekendDays: number[]
): Promise<TimezoneChangeResponse> {
  if (import.meta.env.DEV)
    console.log(`[User Sync] Requesting timezone change for user ${userId} to ${newTimezone}`);

  const { data, error } = await supabase.rpc('request_timezone_change', {
    p_user_id: userId,
    p_new_timezone: newTimezone,
    p_new_weekend_days: newWeekendDays,
  });

  if (error) {
    console.error('[User Sync] Error requesting timezone change:', error);
    throw new Error(`Failed to request timezone change: ${error.message}`);
  }

  const result = data as TimezoneChangeResponse;
  if (import.meta.env.DEV) console.log(`[User Sync] Timezone change request result:`, result);

  return result;
}

interface WeekendStatusResponse {
  isWeekend: boolean;
  dayOfWeek: number;
  weekendDays: number[];
  userLocalTime: string;
  serverUtcTime: string;
  timezone: string;
}

/**
 * Check if it's currently weekend for a user (server-authoritative)
 */
export async function isWeekendForUser(userId: string): Promise<WeekendStatusResponse> {
  const { data, error } = await supabase.rpc('is_weekend_for_user', {
    p_user_id: userId,
  });

  if (error) {
    console.error('[User Sync] Error checking weekend status:', error);
    throw new Error('Failed to verify weekend status with server');
  }

  const result = data as WeekendStatusResponse;
  return result;
}
